import { type Href, router } from 'expo-router';
import { useCallback, useRef } from 'react';

import { routes } from '@/constants/routes';
import { storage, storageKeys } from '@/constants/storage';

/**
 * dismissTo (not dismiss): the auth flow is a nested pushed stack on top of the
 * originating screen; dismissTo pops everything back to the existing screen
 * instance, preserving its state. The destination screen's focus effect clears
 * `authReturnPath` and reopens the sheet. Falls back to home when no return
 * path was stored.
 *
 * The return path is snapshotted at mount, not read at redirect time. Apple
 * sign-in redirects from a delayed effect (it waits on a follow-up customer
 * query); by then the now-authenticated state can re-focus the destination
 * screen, whose focus effect removes `authReturnPath` first. Reading live would
 * lose that race and fall back to home (SQR-12952). The key is always present
 * when the auth screen mounts, so the snapshot is correct.
 */
export function useAuthRedirect(): { redirectAfterAuth: () => void } {
  const returnPathRef = useRef(storage.getString(storageKeys.authReturnPath));

  const redirectAfterAuth = useCallback(() => {
    const returnPath = returnPathRef.current;

    if (returnPath) {
      router.dismissTo(returnPath as Href);
    } else {
      router.dismissTo(routes.home);
    }
  }, []);

  return { redirectAfterAuth };
}
